'use client';

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * Angle/render image sizing — a faithful port of the Apple app's rules
 * (AngleImageDisplay.swift / AngleViewerSection.swift / ConceptContentView.swift /
 * ConceptPageLayout.swift). Keep the two platforms in lockstep: any change to a
 * clamp, cap, or floor must be mirrored on Apple/Android.
 *
 * The container is shaped to the ANGLE aspect (clamped 3:4..16:9) and bounded by
 * BOTH the column width and the height cap — `.aspectRatio(_, .fit)` — so it
 * always preserves the image proportions and is centered (side gray strips when
 * narrower than the column). The image fills that exactly-shaped box, so the
 * whole image is always visible — nothing is ever cropped by the container.
 */

/** Aspect-ratio clamp bounds — Apple `min(16/9, max(3/4, ratio))`. */
export const ASPECT_MIN = 3 / 4; // 0.75  — portrait floor
export const ASPECT_MAX = 16 / 9; // 1.778 — landscape ceiling

/**
 * minHeight floor — the block never collapses below this, even on a very short
 * browser window (the page scrolls instead). Higher than Apple's 200/220 default
 * because the web window can be dragged arbitrarily short.
 */
const MIN_BLOCK_HEIGHT = 360;

/**
 * Clamp a raw width/height ratio to the 3:4..16:9 range. Falls back to 16:9
 * when no ratio is known yet, matching Apple's `effectiveAspectRatio` default.
 */
export function clampAspectRatio(ratio: number | undefined | null): number {
  if (!ratio || ratio <= 0) return ASPECT_MAX;
  return Math.max(ASPECT_MIN, Math.min(ASPECT_MAX, ratio));
}

export interface ImageSizingConstraints {
  /** Max image height in px — Apple's reserve-based cap. */
  maxImageHeight: number;
  /** minHeight floor in px — the block never collapses below this. */
  minImageHeight: number;
  /** True when the image is stacked above the settings (no inspector column). */
  isSingleColumn: boolean;
  /** Whether the client-side measurement has run (false during SSR). */
  isReady: boolean;
}

interface UseImageConstraintsOptions {
  /** True when the concept editor shows the two-column (inspector) layout. */
  isTwoColumn?: boolean;
}

/**
 * Compute the per-layout image height cap, mirroring Apple:
 *
 * - Two-column (iPad/desktop inspector) — `ConceptContentView.calculateMaxImageHeight`:
 *     reserve = min(viewportH * 0.06, 60); cap = viewportH - 100 - reserve
 * - Single-column (phone/stacked) — `ConceptPageLayout`:
 *     cap = viewportH * 0.5   // half the viewport so the settings peek below
 *
 * Both are floored at {@link MIN_BLOCK_HEIGHT} so the block never gets too short.
 */
export function useImageConstraints(
  options?: UseImageConstraintsOptions
): ImageSizingConstraints {
  const isTwoColumn = options?.isTwoColumn ?? false;

  const [state, setState] = useState<ImageSizingConstraints>(() =>
    computeCap(800, isTwoColumn, false)
  );

  useEffect(() => {
    const calculate = () => {
      setState(computeCap(window.innerHeight, isTwoColumn, true));
    };

    calculate();

    window.addEventListener('resize', calculate);
    window.addEventListener('orientationchange', calculate);

    return () => {
      window.removeEventListener('resize', calculate);
      window.removeEventListener('orientationchange', calculate);
    };
  }, [isTwoColumn]);

  return state;
}

function computeCap(
  viewportHeight: number,
  isTwoColumn: boolean,
  isReady: boolean
): ImageSizingConstraints {
  let maxImageHeight: number;
  if (isTwoColumn) {
    // Hybrid reserve: 6% of viewport capped at 60px, plus 100px static chrome.
    const reserve = Math.min(viewportHeight * 0.06, 60);
    maxImageHeight = viewportHeight - 100 - reserve;
  } else {
    // Cap the stacked image header at half the viewport so the settings peek
    // below it (Apple `geometry.size.height * 0.5`).
    maxImageHeight = viewportHeight * 0.5;
  }

  return {
    maxImageHeight: Math.max(MIN_BLOCK_HEIGHT, maxImageHeight),
    minImageHeight: MIN_BLOCK_HEIGHT,
    isSingleColumn: !isTwoColumn,
    isReady,
  };
}

export interface ImageContainerStyleResult {
  /** Inline style for the sized image container (exact-aspect box). */
  style: CSSProperties;
  /**
   * True when the container is narrower than the column (side gray strips) —
   * only meaningful once the column width is measured.
   */
  needsVerticalPadding: boolean;
}

/**
 * Build the exactly-shaped container box from a clamped aspect ratio and the
 * sizing constraints — the CSS equivalent of Apple's
 * `.aspectRatio(aspect, .fit).frame(maxHeight:).frame(maxWidth:.infinity)`.
 *
 * The box preserves the image ratio and is bounded by BOTH the column width and
 * the height cap (whichever binds first), then centered. It is NEVER stretched
 * away from the ratio, so `object-cover` inside it fills exactly and the whole
 * image stays visible — the container crops nothing.
 *
 * The minHeight floor is enforced on the centering wrapper (not the box), so a
 * short box is padded with gray rather than stretched/cropped.
 *
 * @param cardWidth Measured available column width in px (0 when unknown — a
 *                  ratio-shaped fallback is used until it is measured).
 */
export function computeImageContainerStyle(
  aspectRatio: number,
  constraints: ImageSizingConstraints,
  cardWidth: number
): ImageContainerStyleResult {
  const { maxImageHeight } = constraints;

  // Before the column width is measured, shape by aspect (bounded by the cap and
  // the column) — settles to the exact px box on the next frame.
  if (cardWidth <= 0) {
    return {
      style: {
        aspectRatio: aspectRatio.toString(),
        width: 'auto',
        maxWidth: '100%',
        maxHeight: `${maxImageHeight}px`,
        margin: '0 auto',
      },
      needsVerticalPadding: false,
    };
  }

  // `.fit` inside (cardWidth × maxImageHeight): the box preserves the image ratio
  // and shrinks to fit whichever dimension binds first.
  const widthBoundHeight = cardWidth / aspectRatio;
  let height = Math.min(widthBoundHeight, maxImageHeight);
  let width = height * aspectRatio;
  if (width > cardWidth) {
    width = cardWidth;
    height = width / aspectRatio;
  }

  const needsVerticalPadding = width + 0.5 < cardWidth;

  return {
    style: {
      width: `${Math.round(width)}px`,
      height: `${Math.round(height)}px`,
      margin: '0 auto',
    },
    needsVerticalPadding,
  };
}
